import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../App';

const INTEGRATION_TYPES = [
  { value: 'rest_api',  label: 'REST API' },
  { value: 'database',  label: 'Database endpoint' },
  { value: 'erp',       label: 'ERP (SAP / Oracle)' },
  { value: 'crm',       label: 'CRM (Salesforce / HubSpot)' },
  { value: 'ai_llm',    label: '🤖 AI / LLM API' },
  { value: 'ftp',       label: 'FTP / SFTP server' },
  { value: 'custom',    label: 'Custom / Legacy' },
];

const AI_PROVIDERS = [
  { value: 'openai',    label: 'OpenAI (GPT-4, GPT-3.5)' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'gemini',    label: 'Google Gemini' },
  { value: 'custom',    label: 'Custom LLM / Self-hosted' },
];

const PRESETS = [
  { name: 'GitHub API',       url: 'https://api.github.com',                           type: 'rest_api',  method: 'GET',  expectedStatus: 200 },
  { name: 'Salesforce',       url: 'https://your-domain.salesforce.com/services/data/v59.0/', type: 'crm', method: 'GET', expectedStatus: 200 },
  { name: 'OpenAI GPT',       url: 'https://api.openai.com/v1/chat/completions',        type: 'ai_llm',   method: 'POST', expectedStatus: 200, aiProvider: 'openai',    aiModel: 'gpt-3.5-turbo' },
  { name: 'Anthropic Claude', url: 'https://api.anthropic.com/v1/messages',             type: 'ai_llm',   method: 'POST', expectedStatus: 200, aiProvider: 'anthropic', aiModel: 'claude-haiku-4-5-20251001' },
  { name: 'Google Gemini',    url: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent', type: 'ai_llm', method: 'POST', expectedStatus: 200, aiProvider: 'gemini' },
  { name: 'SAP Health',       url: 'https://your-sap-host/sap/bc/ping',                 type: 'erp',      method: 'GET',  expectedStatus: 200 },
  { name: 'Custom REST API',  url: '',                                                  type: 'rest_api',  method: 'GET',  expectedStatus: 200 },
];

const INITIAL = {
  name: '', description: '', type: 'rest_api', url: '', method: 'GET',
  expectedStatus: 200, pollIntervalSeconds: 60,
  headers: '', authType: 'none', apiKey: '', basicUser: '', basicPass: '',
  aiProvider: 'openai', aiModel: '',
};

export default function AddIntegration() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function applyPreset(preset) {
    setForm(prev => ({ ...prev, ...preset, headers: '', authType: 'none', apiKey: '' }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) return setError('Integration name is required');
    if (!form.url.trim()) return setError('URL is required');
    try { new URL(form.url); } catch { return setError('Please enter a valid URL (include https://)'); }

    setLoading(true);
    try {
      let headers = {};
      try { if (form.headers.trim()) headers = JSON.parse(form.headers); }
      catch { return setError('Headers must be valid JSON. Example: {"X-Custom": "value"}'); }

      const credentials = {};
      if (form.authType === 'api_key') credentials.apiKey = form.apiKey;
      if (form.authType === 'basic') credentials.basicAuth = { user: form.basicUser, pass: form.basicPass };

      if (form.type === 'ai_llm' && form.authType === 'api_key') {
        credentials.apiKey = form.apiKey;
      }

      // Anthropic needs extra header
      if (form.aiProvider === 'anthropic') {
        headers['anthropic-version'] = '2023-06-01';
        headers['x-api-key'] = form.apiKey;
        delete credentials.apiKey;
      }

      await apiCall('/api/integrations', {
        method: 'POST',
        body: {
          name: form.name, description: form.description,
          type: form.type, url: form.url, method: form.method,
          expectedStatus: parseInt(form.expectedStatus),
          pollIntervalSeconds: parseInt(form.pollIntervalSeconds),
          headers, credentials,
          ...(form.type === 'ai_llm' && { aiProvider: form.aiProvider, aiModel: form.aiModel }),
        },
      });

      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const isAI = form.type === 'ai_llm';

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Add integration</h1>
          <p className="page-sub">Monitor any API, system, or AI endpoint — starts checking immediately</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <h2 className="card-title" style={{ marginBottom: 12 }}>Quick start</h2>
        <div className="preset-grid">
          {PRESETS.map(preset => (
            <button key={preset.name} className="preset-btn" onClick={() => applyPreset(preset)}>
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      <form className="card" onSubmit={handleSubmit}>
        <h2 className="card-title" style={{ marginBottom: 18 }}>Integration details</h2>

        {error && <div className="form-error" style={{ marginBottom: 16 }}>{error}</div>}

        <div className="form-row two-col">
          <div className="form-group">
            <label className="form-label">Integration name *</label>
            <input className="form-input" type="text" placeholder="e.g. OpenAI Production API" value={form.name} onChange={e => set('name', e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Type</label>
            <select className="form-select" value={form.type} onChange={e => set('type', e.target.value)}>
              {INTEGRATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        {isAI && (
          <div className="form-row two-col" style={{ padding: '12px 14px', background: 'var(--purple-dim)', borderRadius: 8, marginBottom: 16, border: '1px solid var(--purple-glow)' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">AI Provider</label>
              <select className="form-select" value={form.aiProvider} onChange={e => set('aiProvider', e.target.value)}>
                {AI_PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Model (optional)</label>
              <input className="form-input" type="text" placeholder="e.g. gpt-4, claude-3-5-sonnet-20241022" value={form.aiModel} onChange={e => set('aiModel', e.target.value)} />
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Description</label>
          <input className="form-input" type="text" placeholder="What does this integration do?" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>

        <div className="form-row two-col">
          <div className="form-group" style={{ flex: 3 }}>
            <label className="form-label">Endpoint URL *</label>
            <input className="form-input mono" type="url" placeholder="https://api.openai.com/v1/chat/completions" value={form.url} onChange={e => set('url', e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">HTTP Method</label>
            <select className="form-select" value={form.method} onChange={e => set('method', e.target.value)}>
              {['GET', 'POST', 'PUT', 'HEAD'].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row two-col">
          <div className="form-group">
            <label className="form-label">Expected HTTP status</label>
            <input className="form-input" type="number" min="100" max="599" value={form.expectedStatus} onChange={e => set('expectedStatus', e.target.value)} />
            <span className="form-hint">200 = success, 401 = needs auth but is alive</span>
          </div>
          <div className="form-group">
            <label className="form-label">Check every</label>
            <select className="form-select" value={form.pollIntervalSeconds} onChange={e => set('pollIntervalSeconds', e.target.value)}>
              <option value={30}>30 seconds (Pro)</option>
              <option value={60}>1 minute</option>
              <option value={120}>2 minutes</option>
              <option value={300}>5 minutes</option>
              <option value={600}>10 minutes</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Authentication</label>
          <select className="form-select" style={{ marginBottom: 10 }} value={form.authType} onChange={e => set('authType', e.target.value)}>
            <option value="none">No authentication</option>
            <option value="api_key">API Key / Bearer token</option>
            <option value="basic">Basic auth (username + password)</option>
          </select>

          {form.authType === 'api_key' && (
            <input className="form-input mono" type="password" placeholder={isAI ? 'Paste your API key (sk-... or similar)' : 'API key or Bearer token'} value={form.apiKey} onChange={e => set('apiKey', e.target.value)} />
          )}
          {form.authType === 'basic' && (
            <div className="form-row two-col">
              <input className="form-input" type="text" placeholder="Username" value={form.basicUser} onChange={e => set('basicUser', e.target.value)} />
              <input className="form-input" type="password" placeholder="Password" value={form.basicPass} onChange={e => set('basicPass', e.target.value)} />
            </div>
          )}
        </div>

        {!isAI && (
          <div className="form-group">
            <label className="form-label">Custom headers (JSON, optional)</label>
            <textarea className="form-input mono" rows={3} placeholder={'{"X-API-Version": "2024", "Accept": "application/json"}'} value={form.headers} onChange={e => set('headers', e.target.value)} />
          </div>
        )}

        {isAI && (
          <div style={{ padding: '10px 14px', background: 'var(--green-dim)', borderRadius: 8, fontSize: 12, color: 'var(--green)', marginBottom: 16, border: '1px solid rgba(62,207,164,0.2)' }}>
            AI monitoring automatically sends a test prompt every check and verifies the response is valid, non-empty, and within normal latency range.
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/dashboard')}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Creating...' : 'Create integration'}</button>
        </div>
      </form>
    </div>
  );
}
